/**
 * TechnologySearchForm - Shared component for production tool pages
 *
 * Provides client, technology, configuration profile, and serial number selection
 * with cascading dropdowns:
 *   Client -> Technology (filters active profiles by client)
 *   Technology -> Profile (filters active profiles by client and technology)
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import type { Account } from '@/api/commercial/accounts';
import { configurationProfileApi } from '@/api/production/configuration-profiles';
import { type ITechnology, technologyApi } from '@/api/production/technologies';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { IConfigurationProfile } from '@/types/production/configuration-profile';

const technologySearchSchema = z.object({
  client_id: z.string().optional(),
  technology_id: z.string().optional(),
  configuration_profile_id: z.string().optional(),
  serial_number: z.string().optional(),
});

export type TechnologySearchFormData = z.infer<typeof technologySearchSchema>;

export interface TechnologySearchFormProps {
  technology?: ITechnology | null;
  configurationProfile?: IConfigurationProfile | null;
  serialNumber?: string;
  showClient?: boolean;
  showConfigurationProfile?: boolean;
  showSerialNumber?: boolean;
  onValuesChange?: (values: TechnologySearchFormData) => void;
  onClientChange?: (client: Account | null) => void;
  onTechnologyChange?: (technology: ITechnology | null) => void;
  onConfigurationProfileChange?: (profile: IConfigurationProfile | null) => void;
  onSerialNumberChange?: (serial: string) => void;
}

function documentType(value?: string): Account['document']['type'] {
  return value && value.replace(/\D/g, '').length > 11 ? 'cnpj' : 'cpf';
}

function toUniqueAccounts(profiles: IConfigurationProfile[]): Account[] {
  const accounts = new Map<string, Account>();

  for (const profile of profiles) {
    if (!profile.client_id || !profile.client) continue;

    const documentValue = profile.client.document?.value ?? '';
    accounts.set(profile.client_id, {
      id: profile.client_id,
      document: {
        value: documentValue,
        type: documentType(documentValue),
      },
      name: profile.client.company_name,
      fantasy_name: profile.client.trade_name,
      created_at: '',
    });
  }

  return Array.from(accounts.values());
}

function toUniqueTechnologies(profiles: IConfigurationProfile[]): ITechnology[] {
  const technologies = new Map<string, ITechnology>();

  for (const profile of profiles) {
    if (!profile.technology) continue;
    technologies.set(profile.technology.id, profile.technology);
  }

  return Array.from(technologies.values());
}

export function TechnologySearchForm({
  technology,
  configurationProfile,
  serialNumber = '',
  showClient = false,
  showConfigurationProfile = true,
  showSerialNumber = false,
  onValuesChange,
  onClientChange,
  onTechnologyChange,
  onConfigurationProfileChange,
  onSerialNumberChange,
}: TechnologySearchFormProps) {
  const [selectedTechnologyId, setSelectedTechnologyId] = React.useState<string | undefined>(
    technology?.id,
  );
  const [selectedClientId, setSelectedClientId] = React.useState<string | undefined>();
  const [clientSearch, setClientSearch] = React.useState('');
  const [profileSearchTerm, setProfileSearchTerm] = React.useState('');

  const form = useForm<TechnologySearchFormData>({
    resolver: zodResolver(technologySearchSchema),
    defaultValues: {
      client_id: '',
      technology_id: technology?.id ?? '',
      configuration_profile_id: configurationProfile?.id ?? '',
      serial_number: serialNumber,
    },
  });

  const technologyQuery = useQuery({
    queryKey: ['technologies', 'list'],
    queryFn: async () => {
      const result = await technologyApi.findMany({ limit: 100 });
      return result.docs;
    },
    enabled: !showClient,
  });

  const clientQuery = useQuery({
    queryKey: ['configuration-profiles-active-clients', clientSearch],
    queryFn: async () => {
      const result = await configurationProfileApi.findMany({
        status: 'ATIVO',
        search: clientSearch,
        limit: 100,
      });
      return toUniqueAccounts(result.docs);
    },
    enabled: showClient && clientSearch.length >= 2,
    staleTime: 60 * 1000,
  });

  const activeClientProfilesQuery = useQuery({
    queryKey: ['configuration-profiles-active-by-client', selectedClientId],
    queryFn: async () => {
      const result = await configurationProfileApi.findMany({
        status: 'ATIVO',
        client_id: selectedClientId,
        limit: 100,
      });
      return result.docs;
    },
    enabled: showClient && !!selectedClientId,
    staleTime: 60 * 1000,
  });

  const filteredTechnologies = React.useMemo(() => {
    const technologies = showClient
      ? toUniqueTechnologies(activeClientProfilesQuery.data ?? [])
      : (technologyQuery.data ?? []);

    if (!Array.isArray(technologies)) return [];
    return technologies.filter((tech) => tech.name.system !== 'DM_E3_PLUS');
  }, [activeClientProfilesQuery.data, showClient, technologyQuery.data]);

  const configurationProfileQuery = useQuery({
    queryKey: [
      'configuration-profiles-active-by-client-technology',
      selectedClientId,
      selectedTechnologyId,
      profileSearchTerm,
      showClient,
    ],
    queryFn: async () => {
      const result = await configurationProfileApi.findMany({
        status: 'ATIVO',
        client_id: showClient ? selectedClientId : undefined,
        technology_id: selectedTechnologyId,
        search: profileSearchTerm || undefined,
        limit: 100,
      });
      return result.docs;
    },
    enabled:
      showConfigurationProfile && !!selectedTechnologyId && (!showClient || !!selectedClientId),
  });

  const filteredProfiles = configurationProfileQuery.data ?? [];

  const handleClientChange = React.useCallback(
    (selected: Account[]) => {
      const client = selected[0] ?? null;
      const clientId = client?.id ?? '';
      setSelectedClientId(clientId);
      setSelectedTechnologyId(undefined);
      setProfileSearchTerm('');
      form.setValue('client_id', clientId);
      form.setValue('technology_id', '');
      form.setValue('configuration_profile_id', '');

      onClientChange?.(client);
      onTechnologyChange?.(null);
      onConfigurationProfileChange?.(null);

      onValuesChange?.({
        ...form.getValues(),
        client_id: clientId,
        technology_id: '',
        configuration_profile_id: '',
      });
    },
    [form, onClientChange, onTechnologyChange, onConfigurationProfileChange, onValuesChange],
  );

  const handleTechnologyChange = React.useCallback(
    (technologyId: string) => {
      setSelectedTechnologyId(technologyId);
      setProfileSearchTerm('');
      form.setValue('technology_id', technologyId);
      form.setValue('configuration_profile_id', '');

      const selectedTech = filteredTechnologies.find((tech) => tech.id === technologyId) ?? null;
      onTechnologyChange?.(selectedTech);
      onConfigurationProfileChange?.(null);

      const values = form.getValues();
      onValuesChange?.({
        ...values,
        technology_id: technologyId,
        configuration_profile_id: '',
      });
    },
    [filteredTechnologies, form, onTechnologyChange, onConfigurationProfileChange, onValuesChange],
  );

  const handleConfigurationProfileChange = React.useCallback(
    (profiles: IConfigurationProfile[]) => {
      const profile = profiles[0] ?? null;
      form.setValue('configuration_profile_id', profile?.id ?? '');
      onConfigurationProfileChange?.(profile);

      const values = form.getValues();
      onValuesChange?.({
        ...values,
        configuration_profile_id: profile?.id ?? '',
      });
    },
    [form, onConfigurationProfileChange, onValuesChange],
  );

  const handleSerialNumberChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      form.setValue('serial_number', value);
      onSerialNumberChange?.(value);

      const values = form.getValues();
      onValuesChange?.({
        ...values,
        serial_number: value,
      });
    },
    [form, onSerialNumberChange, onValuesChange],
  );

  const gridCols = React.useMemo(() => {
    let cols = 1;
    if (showClient) cols++;
    if (showConfigurationProfile) cols++;
    if (showSerialNumber) cols++;
    return cols;
  }, [showClient, showConfigurationProfile, showSerialNumber]);

  return (
    <form className="space-y-4">
      <div
        className={`grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-${gridCols === 1 ? '2' : gridCols === 2 ? '4' : gridCols === 3 ? '6' : '8'}`}
      >
        {showClient && (
          <div className="sm:col-span-2">
            <Label className="block text-sm/6 font-medium text-gray-900">Cliente</Label>
            <div className="mt-2">
              <Controller
                control={form.control}
                name="client_id"
                render={({ field }) => (
                  <Combobox<Account>
                    data={clientQuery.data ?? []}
                    displayValueGetter={(client) =>
                      client.fantasy_name || client.name || client.document?.value || ''
                    }
                    keyExtractor={(client) => client.id}
                    type="single"
                    disableLocalFilter
                    isLoading={clientQuery.isFetching}
                    onSearchChange={(query) => setClientSearch(query)}
                    onOptionChange={(selected) => {
                      handleClientChange(selected);
                      field.onChange(selected[0]?.id ?? '');
                    }}
                    placeholder="Buscar cliente com perfil ativo..."
                  />
                )}
              />
            </div>
          </div>
        )}

        <div className={showConfigurationProfile ? 'sm:col-span-2' : 'sm:col-span-2'}>
          <Label className="block text-sm/6 font-medium text-gray-900">Tecnologia</Label>
          <div className="mt-2">
            <Select
              onValueChange={handleTechnologyChange}
              defaultValue={technology?.id}
              value={selectedTechnologyId}
              disabled={showClient && !selectedClientId}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    showClient && !selectedClientId
                      ? 'Selecione um cliente primeiro'
                      : 'Selecione uma tecnologia'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(technologyQuery.isFetching || activeClientProfilesQuery.isFetching) && (
                  <div className="h-20 flex flex-col text-xs items-center justify-center text-muted-foreground">
                    <Loader2 className="animate-spin text-muted-foreground" size={16} />
                    <span className="mt-2">Buscando tecnologias disponíveis</span>
                  </div>
                )}
                {filteredTechnologies.map((tech) => (
                  <SelectItem key={tech.id} value={tech.id}>
                    {tech.name.brand}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {showConfigurationProfile && (
          <div className="sm:col-span-4">
            <Label className="block text-sm/6 font-medium text-gray-900">
              Perfil de configuração
            </Label>
            <div className="mt-2">
              <Controller
                control={form.control}
                name="configuration_profile_id"
                render={({ field }) => (
                  <Combobox<IConfigurationProfile>
                    data={filteredProfiles}
                    displayValueGetter={(doc) => doc.name}
                    keyExtractor={(doc) => doc.id}
                    type="single"
                    value={
                      configurationProfile
                        ? [configurationProfile]
                        : field.value
                          ? filteredProfiles.filter((profile) => profile.id === field.value)
                          : []
                    }
                    onSearchChange={(query) => setProfileSearchTerm(query)}
                    onOptionChange={handleConfigurationProfileChange}
                    placeholder={
                      !selectedTechnologyId
                        ? 'Selecione uma tecnologia primeiro'
                        : 'Escolha o perfil de configuração'
                    }
                    isLoading={configurationProfileQuery.isFetching}
                    disableLocalFilter
                    disabled={!selectedTechnologyId}
                  />
                )}
              />
            </div>
          </div>
        )}

        {showSerialNumber && (
          <div className="sm:col-span-2">
            <Label className="block text-sm/6 font-medium text-gray-900">
              Número de série do dispositivo
            </Label>
            <div className="mt-2">
              <Input
                placeholder="Digite o número de série"
                value={form.watch('serial_number')}
                onChange={handleSerialNumberChange}
              />
            </div>
          </div>
        )}
      </div>
    </form>
  );
}

export default TechnologySearchForm;
